import { type FC, useState } from "react";
import { useParams } from "react-router-dom";
import { usePropertyQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyQuery";
import { useIsViewer } from "web_myplann/Api/Rights/RightsProvider";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioDetailMenu } from "web_myplann/Portfolio/Components/PortfolioDetailMenu";
import { DetailEmptyState } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailEmptyState";
import { DetailHero } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailHero";
import { DetailLayout } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLayout";
import { DetailRemindersSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRemindersSection";
import { MarketPriceHistoryDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/MarketPriceHistoryDrawer";
import { PropertyDetailSections } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/PropertyDetailSections";
import { getPropertySection } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/Property/propertySectionRegistry";
import { DetailSkeleton } from "web_myplann/Portfolio/Components/Redesign/Detail/Skeletons/DetailSkeleton";
import { PropertyValueDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Value/PropertyValueDrawer";
import { getPropertyHeroMeta } from "web_myplann/Portfolio/Helpers/getPropertyHeroMeta";
import { getReminderEntity } from "web_myplann/Portfolio/Helpers/getReminderEntity";
import { hasNoBusinessData } from "web_myplann/Portfolio/Helpers/hasNoBusinessData";
import { usePropertyTitle } from "web_myplann/Portfolio/Hooks/usePropertyTitle";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

export const PropertyDetailPageRedesign: FC = () => {
	const { id } = useParams<{ id: string }>();
	const propertyId = id ? Number(id) : undefined;
	const { t } = useResource();
	const isViewer = useIsViewer();

	const { data: property, isLoading } = usePropertyQuery(propertyId);
	const [isValueDrawerOpen, setIsValueDrawerOpen] = useState(false);
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);

	const title = usePropertyTitle(property, t);

	const currencySign = t(Resources.Common.CurrencySign);

	const showEmpty = !isLoading && hasNoBusinessData(property);

	const TypeDetail =
		property && !showEmpty ? getPropertySection(property) : undefined;

	const propertyType = property?.type ?? undefined;

	const heroMeta = property ? getPropertyHeroMeta(property, t) : undefined;

	const priceHistory = showEmpty ? undefined : heroMeta?.priceHistory;
	const hasPriceHistory = priceHistory !== undefined && priceHistory.length > 0;

	return (
		<DetailLayout
			title={title}
			menu={
				propertyType !== undefined && propertyId !== undefined ? (
					<PortfolioDetailMenu
						kind={PortfolioItemKind.Property}
						id={propertyId}
						propertyType={propertyType}
						title={title}
						onUpdateValue={() => setIsValueDrawerOpen(true)}
					/>
				) : undefined
			}
			hero={
				property && !showEmpty && heroMeta ? (
					<DetailHero
						title={title}
						subtype={heroMeta.subtype}
						address={heroMeta.address}
						propertyType={property.type}
						value={heroMeta.estimatedValue}
						valueLabel={heroMeta.valueLabel}
						currencySign={currencySign}
						valueSource={heroMeta.valueSource}
						evaluationPercent={heroMeta.evaluationPercent}
						forInvestments={property.forInvestments}
						editable={!isViewer}
						onEditValue={() => setIsValueDrawerOpen(true)}
						onShowHistory={
							hasPriceHistory ? () => setIsHistoryOpen(true) : undefined
						}
					/>
				) : undefined
			}
		>
			{isLoading ? (
				<DetailSkeleton />
			) : showEmpty ? (
				<DetailEmptyState />
			) : property && TypeDetail ? (
				<PropertyDetailSections
					property={property}
					TypeDetail={TypeDetail}
					currencySign={currencySign}
					propertyId={propertyId}
				/>
			) : null}

			{!isLoading && property && !TypeDetail && (
				<DetailRemindersSection
					entity={getReminderEntity(PortfolioItemKind.Property, property.id)}
				/>
			)}

			{property && propertyId !== undefined && heroMeta && (
				<PropertyValueDrawer
					open={isValueDrawerOpen}
					onClose={() => setIsValueDrawerOpen(false)}
					propertyId={propertyId}
					propertyType={property.type}
					valueLabel={heroMeta.valueLabel}
					value={heroMeta.estimatedValue}
					currencySign={currencySign}
				/>
			)}

			{hasPriceHistory && priceHistory && (
				<MarketPriceHistoryDrawer
					open={isHistoryOpen}
					onClose={() => setIsHistoryOpen(false)}
					priceHistory={priceHistory}
					currencySign={currencySign}
				/>
			)}
		</DetailLayout>
	);
};
